import yaml from "js-yaml";
import { useEffect, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { PageHeader } from "@ant-design/pro-layout";
import PageContentWrapper from "../../components/PageContentWrapper";
import { currentProjectState } from "../../recoil/project";
import { Template } from "../../types";
import { useCurrentProject } from "../../contexts/Project";
import CloudAccountLabel from "../../components/CloudAccountLabel";
import { Button, Skeleton, Space } from "antd";
import { ArrowLeft, Rocket, Settings, Cloud } from "lucide-react";
import { useQuery } from "../../contexts/Navigation";
import { getTemplate } from "../../utils/get-template";
import { newComponentState } from "../../recoil/environment";
import DeployMultipleComponentsModal from "../../components/modals/DeployMultipleComponentsModal";
import { useEnvironmentDetails } from "../../hooks/queries/environments";

const { Input: SkeletonInput } = Skeleton;

interface EnvironmentRootProps {
    settings?: boolean;
    templates?: boolean;
    existingTemplate?: boolean;
}

const EnvironmentRoot = ({
    settings,
    templates,
    existingTemplate
}: EnvironmentRootProps) => {
    const query = useQuery();
    const { projectId, envId, componentId, namespace, appName, templateId } = useParams() as {
        projectId: string;
        envId: string;
        componentId: string;
        namespace: string;
        appName: string;
        templateId: string;
    };
    const currentProject = useRecoilValue(currentProjectState);
    const newComponentUpdate = useRecoilValue(newComponentState);
    const { updateCurrentProject } = useCurrentProject();
    const [template, setTemplate] = useState<Template>();
    const [showDeployModal, setShowDeployModal] = useState(false);

    // Use TanStack Query for environment details
    const { 
        data: env, 
        error: envError, 
        refetch: refetchEnv 
    } = useEnvironmentDetails(projectId, envId, {
        enabled: !!projectId && !!envId,
    });

    // Check for 404 error
    const notFoundError = (envError as Error & { is404?: boolean })?.is404 === true;

    useEffect(() => {
        // change the current project if the projectId in the URL is different from the current project saved in local storage
        // this is the root component for all Environment related routes, so no need to do this in other places
        if (!!projectId && projectId !== currentProject.id) {
            updateCurrentProject(projectId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        (async () => {
            if (templates) {
                const templateString = await getTemplate(query.get("template")!);
                setTemplate(yaml.load(templateString) as Template);
            }
        })();

    }, [templateId]);

    useEffect(() => {
        if (newComponentUpdate.envId === envId) {
            refetchEnv();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newComponentUpdate]);

    const EnvPageHeader = () => (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
            padding: "32px 50px 24px 50px"
        }}>
            {env ? (
                <div style={{ 
                    display: "flex", 
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0",
                    background: "transparent",
                    gap: "32px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
                            <div style={{ 
                                width: "48px", 
                                height: "48px", 
                                borderRadius: "12px", 
                                background: "linear-gradient(135deg, #077BB5 0%, #0a5a82 100%)", 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(7, 123, 181, 0.2)"
                            }}>
                                <Cloud size={24} color="#fff" />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0 0 4px 0", letterSpacing: "-0.02em" }}>
                                    {env.name}
                                </span>
                                <CloudAccountLabel
                                    provider={env.cloudProvider}
                                    envId={env.id}
                                    accountId={env.awsAccountId || env.gcpProjectId || env.exoscaleAccountId}
                                    region={env.region || env.gcpRegion || env.exoscaleZone}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Button
                            onClick={() => setShowDeployModal(true)}
                            icon={<Rocket size={14} />}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: 500
                            }}
                        >
                            Bulk Deployment
                        </Button>
                        <Link to={`/projects/${projectId}/environments/${envId}/settings`}>
                            <Button 
                                icon={<Settings size={14} />}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                            />
                        </Link>
                    </div>
                </div>
            ) : (
                <div style={{ 
                    display: "flex", 
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0",
                    background: "transparent",
                    gap: "32px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
                            <div style={{ 
                                width: "48px", 
                                height: "48px", 
                                borderRadius: "12px", 
                                background: "linear-gradient(135deg, #077BB5 0%, #0a5a82 100%)", 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "center",
                                boxShadow: "0 4px 12px rgba(7, 123, 181, 0.2)"
                            }}>
                                <Cloud size={24} color="#fff" style={{ opacity: 0.3 }} />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <Skeleton.Input 
                                    active 
                                    size="small" 
                                    style={{ 
                                        width: 150, 
                                        height: 20,
                                        borderRadius: "4px"
                                    }} 
                                />
                                <Skeleton.Input 
                                    active 
                                    size="small" 
                                    style={{ 
                                        width: 200, 
                                        height: 14,
                                        borderRadius: "4px"
                                    }} 
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Skeleton.Button 
                            active 
                            size="small" 
                            style={{ 
                                width: 140, 
                                height: 32,
                                borderRadius: "6px"
                            }} 
                        />
                        <Skeleton.Button 
                            active 
                            size="small" 
                            style={{ 
                                width: 32, 
                                height: 32,
                                borderRadius: "6px"
                            }} 
                        />
                    </div>
                </div>
            )}
            {
                showDeployModal &&
                <DeployMultipleComponentsModal
                    projectId={projectId}
                    envId={envId}
                    open={showDeployModal}
                    onCancel={() => setShowDeployModal(false)}
                />
            }
        </div>
    )

    const SettingsPageHeader = () => (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
            padding: "32px 50px 24px 50px"
        }}>
            {env ? (
                <div style={{ 
                    display: "flex", 
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0",
                    background: "transparent",
                    gap: "32px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                        <Link to={
                            appName ? `/projects/${projectId}/environments/${envId}/components/${componentId}/apps/${namespace}/${appName}/overview` :
                                componentId ?
                                    `/projects/${projectId}/environments/${envId}/components/${componentId}/deployments` :
                                    `/projects/${projectId}/environments/${envId}`
                        }>
                            <Button 
                                icon={<ArrowLeft size={16} />}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0
                                }}
                            />
                        </Link>
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "16px",
                            padding: "0",
                            background: "transparent"
                        }}>
                            <div style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "12px",
                                background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)"
                            }}>
                                <Settings size={24} color="#fff" />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontSize: "16px", color: "#6b7280", margin: "0 0 4px 0", fontWeight: 500 }}>
                                    {
                                        appName ? "App settings" :
                                            componentId ? "Component settings" :
                                                "Environment settings"
                                    }
                                </span>
                                <span style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0", letterSpacing: "-0.02em" }}>
                                    {appName || componentId || env.name}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ 
                    display: "flex", 
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0",
                    background: "transparent",
                    gap: "32px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                        <Skeleton.Button 
                            active 
                            size="small" 
                            style={{ 
                                width: 32, 
                                height: 32,
                                borderRadius: "6px",
                                flexShrink: 0
                            }} 
                        />
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: "16px",
                            padding: "0",
                            background: "transparent"
                        }}>
                            <Skeleton.Button 
                                active 
                                size="small" 
                                style={{ 
                                    width: 48, 
                                    height: 48,
                                    borderRadius: "12px",
                                    flexShrink: 0
                                }} 
                            />
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <Skeleton.Input 
                                    active 
                                    size="small" 
                                    style={{ 
                                        width: 150, 
                                        height: 16,
                                        borderRadius: "4px"
                                    }} 
                                />
                                <Skeleton.Input 
                                    active 
                                    size="small" 
                                    style={{ 
                                        width: 200, 
                                        height: 20,
                                        borderRadius: "4px"
                                    }} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )

    const TemplatesPageHeader = () => (
        <PageHeader>
            {
                env && template ?
                    <Space size="large">
                        <Link to={
                            appName ? `/projects/${projectId}/environments/${envId}/components/${componentId}/apps/${namespace}/${appName}/overview` :
                                componentId ?
                                    `/projects/${projectId}/environments/${envId}/components/${componentId}/deployments` :
                                    `/projects/${projectId}/environments/${envId}`
                        }>
                            <Button icon={<ArrowLeft size={16} />} />
                        </Link>
                        <div>
                            <div style={{ fontSize: "16px", }}>
                                Deploy template
                            </div>
                            <div style={{ fontSize: "20px", fontWeight: 600 }}>
                                {template?.name}
                            </div>
                        </div>
                    </Space> :
                    <SkeletonInput />
            }
        </PageHeader>
    )

    const ExistingTemplatesPageHeader = () => (
        <PageHeader>
            {
                env ?
                    <Space size="large">
                        <Link to={
                            appName ? `/projects/${projectId}/environments/${envId}/components/${componentId}/apps/${namespace}/${appName}/overview` :
                                componentId ?
                                    `/projects/${projectId}/environments/${envId}/components/${componentId}/deployments` :
                                    `/projects/${projectId}/environments/${envId}`
                        }>
                            <Button icon={<ArrowLeft size={16} />} />
                        </Link>
                        <div>
                            <div style={{ fontSize: "16px", }}>
                                Deploy ready template
                            </div>
                            <div style={{ fontSize: "20px", fontWeight: 600 }}>
                                {query.get("templateName") || templateId}
                            </div>
                        </div>
                    </Space> :
                    <SkeletonInput />
            }
        </PageHeader>
    )

    if (notFoundError) {
        return (
            <PageContentWrapper
                notFoundError={notFoundError}
            >
                <></>
            </PageContentWrapper>
        );
    }

    if (settings) {
        return (
            <PageContentWrapper
                size="medium"
                header={<SettingsPageHeader />}
            >
                <Outlet context={{ env, refetchEnv }} />
            </PageContentWrapper>
        );
    }

    if (templates) {
        return (
            <PageContentWrapper
                size="medium"
                header={<TemplatesPageHeader />}
            >
                <Outlet context={{ env, refetchEnv }} />
            </PageContentWrapper>
        );
    }

    if (existingTemplate) {
        return (
            <PageContentWrapper
                size="medium"
                header={<ExistingTemplatesPageHeader />}
            >
                <Outlet context={{ env, refetchEnv }} />
            </PageContentWrapper>
        );
    }

    return (
        <div style={{ margin: 0, padding: 0, width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
                <EnvPageHeader />
                <div style={{ padding: '50px 50px' }}>
                    <Outlet context={{ env, refetchEnv }} />
                </div>
            </div>
        </div>
    );
}

export default EnvironmentRoot;
